// Formulario de captación de leads para la vista pública de propiedad
// Al enviar, crea un invite en Supabase y manda el email de acceso

import React, { useState } from 'react';
import { User, Mail, Phone, Send, CheckCircle, FileText, Map, BarChart2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { createInvite } from '../../services/supabaseService';

const generateToken = () =>
  Math.random().toString(36).substring(2) + Date.now().toString(36);

export const LeadCaptureCard = ({ property }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [status, setStatus] = useState('idle'); // 'idle' | 'sending' | 'success' | 'error'
  const [error, setError] = useState('');

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.email.trim()) {
      setError(t('lead.validationError'));
      return;
    }

    setStatus('sending');

    try {
      const token = generateToken();
      const inviteData = {
        propertyId: property.id,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        token,
      };

      await createInvite(inviteData);

      const inviteUrl = `${window.location.origin}/property/${property.id}?token=${token}`;

      await fetch('/api/send-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: inviteData.name,
          email: inviteData.email,
          propertyTitle: property.title,
          propertyDescription: property.description,
          inviteUrl,
          deadline: property.deadline,
        }),
      });

      setStatus('success');
    } catch (err) {
      console.error('Error registrando lead:', err);
      setError(t('lead.errorMessage'));
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-green-500/30 text-center">
        <div className="bg-green-500/10 p-4 rounded-xl inline-block mb-4">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>
        <h3 className="text-lg font-bold text-white mb-2">{t('lead.successTitle')}</h3>
        <p className="text-slate-400 text-sm">{t('lead.successDesc')}</p>
      </div>
    );
  }

  const features = [
    { icon: Map,       key: 'lead.feature1' },
    { icon: BarChart2, key: 'lead.feature2' },
    { icon: FileText,  key: 'lead.feature3' },
  ];

  return (
    <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50">
      <h3 className="text-lg font-bold text-white mb-1">{t('lead.title')}</h3>
      <p className="text-slate-400 text-sm mb-4">{t('lead.subtitle')}</p>

      {/* Lo que se desbloquea */}
      <ul className="space-y-2 mb-5">
        {features.map(({ icon: Icon, key }) => (
          <li key={key} className="flex items-center gap-2 text-sm text-slate-300">
            <Icon className="w-4 h-4 text-amber-400 flex-shrink-0" />
            {t(key)}
          </li>
        ))}
      </ul>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder={t('lead.namePlaceholder')}
            value={form.name}
            onChange={handleChange('name')}
            className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:border-amber-500/50 text-sm"
          />
        </div>

        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="email"
            placeholder={t('lead.emailPlaceholder')}
            value={form.email}
            onChange={handleChange('email')}
            className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:border-amber-500/50 text-sm"
          />
        </div>

        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="tel"
            placeholder={t('lead.phonePlaceholder')}
            value={form.phone}
            onChange={handleChange('phone')}
            className="w-full bg-slate-700/50 border border-slate-600/50 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:border-amber-500/50 text-sm"
          />
        </div>

        {error && <p className="text-red-400 text-xs">{error}</p>}

        <button
          type="submit"
          disabled={status === 'sending'}
          className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 disabled:opacity-50 text-slate-900 font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition-all"
        >
          {status === 'sending' ? (
            <>
              <div className="w-4 h-4 border-2 border-slate-900/40 border-t-slate-900 rounded-full animate-spin" />
              {t('lead.sending')}
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              {t('lead.submitButton')}
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default LeadCaptureCard;
